import React, { useState } from 'react';
import { AlertTriangle, Check, Clock, CornerUpLeft, KeyRound, Stamp, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { PageHeader } from '../../components/admin/PageHeader';
import { Panel } from '../../components/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { useDemo } from '../../contexts/DemoContext';
import { workflowStages } from '../../data/sampleCase';
import { toBn } from '../../utils/bangla';
type Modal = 'approve' | 'return' | 'reject' | null;
export function ApprovalWorkflow() {
  const navigate = useNavigate();
  const {
    stage,
    record,
    completeStage,
    user
  } = useDemo();
  const [modal, setModal] = useState<Modal>(null);
  const [agree, setAgree] = useState(false);
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const approve = () => {
    if (!agree) return setErr('অনুমোদনের পূর্বে ঘোষণাটি নিশ্চিত করুন।');
    if (pin.length !== 6) return setErr('৬ অঙ্কের ডিজিটাল স্বাক্ষর PIN প্রদান করুন।');
    completeStage(Math.max(stage, 5));
    setModal(null);
    toast.success('নোটিশ অনুমোদিত হয়েছে', {
      description: 'নিরাপদ QR নোটিশ তৈরির জন্য প্রস্তুত'
    });
    navigate('/admin/generate');
  };
  const decline = (kind: 'return' | 'reject') => {
    if (!reason.trim()) return setErr('কারণ উল্লেখ করা আবশ্যক।');
    setModal(null);
    if (kind === 'return') toast.warning('সংশোধনের জন্য ফেরত পাঠানো হয়েছে');else toast.error('নোটিশ প্রত্যাখ্যাত হয়েছে');
    navigate('/admin/notices');
  };
  const open = (m: Modal) => {
    setErr('');
    setReason('');
    setPin('');
    setAgree(false);
    setModal(m);
  };
  return <div data-id="element-719" data-mp="2lsq5u5vcv2hw">
      <PageHeader screen="WEB SCREEN 08" title="নোটিশ অনুমোদন ব্যবস্থাপনা" subtitle={`মামলা নং ${record.caseNo} • অনুমোদনকারী: ${user?.name}`} data-id="element-720" data-mp="2lshs6brert5n" />

      {stage < 4 && <div className="mb-6 flex items-start gap-2 rounded-md border border-[#E69A20]/40 bg-[#FDF3E2] p-3 text-sm text-[#8a5a06]" data-id="element-721" data-mp="2cyv1h0225g3b">
          <AlertTriangle className="mt-0.5 h-4 w-4" data-id="element-722" data-mp="281uu8lth27am" /> এই রেকর্ডটির যাচাই এখনও সম্পন্ন হয়নি। প্রকৃত ব্যবস্থায় যাচাই ছাড়া অনুমোদন করা যাবে না (ডেমোতে এগিয়ে যাওয়া যাবে)।
        </div>}

      <Panel title="প্রক্রিয়ার সময়রেখা" bodyClassName="overflow-x-auto p-5" data-id="element-723" data-mp="30qa5qydea7h7">
        <ol className="flex min-w-[1100px] gap-0" data-id="element-724" data-mp="2vp2xyzb958aj">
          {workflowStages.map((s, i) => {
          const done = i < stage;
          const current = i === stage;
          return <li key={s.label} className="relative flex-1 px-2" data-id="element-725" data-mp="1kaz5xyo85u84" data-mpi={i}>
                <div className="flex items-center" data-id="element-726" data-mp="1fbm8kjzq94ra">
                  <span className={`z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${done ? 'border-[#16834A] bg-[#16834A] text-white' : current ? 'border-[#003B71] bg-white text-[#003B71]' : 'border-slate-200 bg-white text-slate-400'}`} data-id="element-727" data-mp="1faupu9r8b1if">
                    {done ? <Check className="h-4 w-4" data-id="element-728" data-mp="1f5lkzwx0h2yk" /> : current ? <Clock className="h-4 w-4" data-id="element-729" data-mp="1f4kbhwvn8oiy" /> : toBn(i + 1)}
                  </span>
                  {i < workflowStages.length - 1 && <span className={`h-0.5 flex-1 ${done ? 'bg-[#16834A]' : 'bg-slate-200'}`} data-id="element-730" data-mp="1jyensf8x6180" />}
                </div>
                <div className={`mt-3 rounded-md border p-3 ${current ? 'border-[#003B71] bg-[#E8F1FA]/50' : 'border-border'}`} data-id="element-731" data-mp="1sx06caee5g8s">
                  <div className="text-sm font-semibold text-[#003B71]" data-id="element-732" data-mp="1xxegpz3gt29z">{s.label}</div>
                  <div className="mt-1" data-id="element-733" data-mp="22vprcc1l3gt9"><StatusBadge label={done ? 'সম্পন্ন' : current ? 'চলমান' : 'অপেক্ষমাণ'} tone={done ? 'success' : current ? 'info' : 'neutral'} data-id="element-734" data-mp="22u4x6nj6lzvx" /></div>
                  <div className="mt-2 text-xs text-slate-600" data-id="element-735" data-mp="8h59060qjxd1">{done || current ? s.officer : '—'}</div>
                  <div className="text-xs text-slate-400" data-id="element-736" data-mp="1yya8821sylp8">{done ? s.at : '—'}</div>
                  {done && <p className="mt-1.5 text-[11.5px] leading-snug text-slate-600" data-id="element-737" data-mp="1tzfo8ws3mr3j">{s.comment}</p>}
                </div>
              </li>;
        })}
        </ol>
      </Panel>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_380px]" data-id="element-738" data-mp="27wuhkclh3j0s">
        <Panel title="অনুমোদনের জন্য রেকর্ড সারসংক্ষেপ" data-id="element-739" data-mp="230nb8ip605ib">
          <dl className="grid gap-x-8 gap-y-3 text-sm md:grid-cols-2" data-id="element-740" data-mp="1fa1hzxjtkpsd">
            {[['মামলা নম্বর', record.caseNo], ['মামলার তারিখ ও সময়', `${record.caseDate}, ${record.caseTime}`], ['গাড়ির নম্বর', record.vehicle], ['মালিকের নাম', record.owner], ['মোবাইল', record.mobile], ['ঠিকানা', record.address], ['অপরাধের ধরন', record.offence], ['অপরাধের স্থান', record.location], ['আইন/ধারা', record.law], ['হাজিরা', `${record.hearingDate}, ${record.hearingTime}`]].map(([k, v]) => <div key={k} className="border-b border-dashed border-border pb-2" data-id="element-741" data-mp="337irbt273oyg">
                <dt className="text-xs text-slate-500" data-id="element-742" data-mp="3802d3db1dqec">{k}</dt>
                <dd className="font-medium" data-id="element-743" data-mp="2u7m9aaab9ocd">{v}</dd>
              </div>)}
          </dl>
          <div className="mt-4 rounded-md bg-[#F4F7FB] p-3 text-sm" data-id="element-744" data-mp="2z6qgivdodc02">
            <span className="font-semibold text-[#006A4E]" data-id="element-745" data-mp="2z6yvcstcyrdw">যাচাইকারীর মন্তব্য: </span>মূল নোটিশের সাথে সকল তথ্য মিলেছে। ঠিকানা ও ধারা সংশোধন যথাযথ। — এসআই নাসরিন আক্তার
          </div>
        </Panel>
        <Panel title="সিদ্ধান্ত" data-id="element-746" data-mp="3qx3j91gbtwjv">
          <p className="mb-4 text-sm text-slate-600" data-id="element-747" data-mp="3cydmxo1c00wk">অনুমোদনের পর স্বয়ংক্রিয়ভাবে Verification ID, নিরাপদ QR টোকেন ও ডিজিটাল স্বাক্ষর সম্বলিত মানসম্মত নোটিশ তৈরি হবে।</p>
          <div className="grid gap-2" data-id="element-748" data-mp="3hzb2rtyvciuw">
            <Button className="h-11 bg-[#16834A] hover:bg-[#126c3d]" onClick={() => open('approve')} data-id="element-749" data-mp="21if8f4ndwmny"><Stamp className="h-4 w-4" data-id="element-750" data-mp="26hrwtttwm1sn" /> অনুমোদন করুন</Button>
            <Button variant="outline" onClick={() => open('return')} data-id="element-751" data-mp="26jcn3796qykp"><CornerUpLeft className="h-4 w-4" data-id="element-752" data-mp="1mg6s2sv72b4c" /> সংশোধনের জন্য ফেরত</Button>
            <Button variant="outline" className="border-[#D93036]/40 text-[#D93036] hover:bg-[#FCE9EA]" onClick={() => open('reject')} data-id="element-753" data-mp="1ieljhjpp0izy"><XCircle className="h-4 w-4" data-id="element-754" data-mp="1dmtpez7bvv52" /> প্রত্যাখ্যান করুন</Button>
          </div>
        </Panel>
      </div>

      <Dialog open={modal === 'approve'} onOpenChange={o => !o && setModal(null)} data-id="element-755" data-mp="18nh1sc8muk1f">
        <DialogContent data-id="element-756" data-mp="13ppbfmnmz5zc">
          <DialogHeader data-id="element-757" data-mp="13r0hxu8guru5">
            <DialogTitle data-id="element-758" data-mp="13ncosw6xo7fy">চূড়ান্ত অনুমোদন নিশ্চিত করুন</DialogTitle>
            <DialogDescription data-id="element-759" data-mp="ypks7ovcfx5f">মামলা নং {record.caseNo} এর জন্য নিরাপদ প্রসিকিউশন নোটিশ তৈরির অনুমতি দেওয়া হবে।</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 px-6 pb-4" data-id="element-760" data-mp="ppxtjau015g2">
            <label className="flex items-start gap-2 text-sm" data-id="element-761" data-mp="pr8xpbrcwf7p">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[#16834A]" checked={agree} onChange={e => {
              setAgree(e.target.checked);
              setErr('');
            }} data-id="element-762" data-mp="2p31wndxpfun2" />
              আমি নিশ্চিত করছি যে যাচাইকৃত তথ্য মূল নোটিশের সাথে সামঞ্জস্যপূর্ণ এবং নোটিশ ইস্যুর জন্য উপযুক্ত।
            </label>
            <div data-id="element-763" data-mp="2wlrnbo8ql0x1">
              <label className="mb-1 flex items-center gap-1.5 text-sm font-medium" data-id="element-764" data-mp="2wl9i4243jccz"><KeyRound className="h-4 w-4" data-id="element-765" data-mp="31d9oodeo93ij" /> ডিজিটাল স্বাক্ষর PIN</label>
              <Input value={pin} onChange={e => {
              setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
              setErr('');
            }} placeholder="••••••" className="font-mono tracking-[0.4em]" inputMode="numeric" data-id="element-766" data-mp="31f48tpi4crlk" />
              <p className="mt-1 text-xs text-slate-500" data-id="element-767" data-mp="28qoxpe23ytfi">ডেমো: যেকোনো ৬ অঙ্ক</p>
            </div>
            {err && <p className="text-xs text-[#D93036]" data-id="element-768" data-mp="2dp9xjcdg7b3f">{err}</p>}
          </div>
          <DialogFooter data-id="element-769" data-mp="2il8ggkg0c0wg">
            <Button variant="outline" onClick={() => setModal(null)} data-id="element-770" data-mp="2mo55ou01yv9d">বাতিল</Button>
            <Button className="bg-[#16834A] hover:bg-[#126c3d]" onClick={approve} data-id="element-771" data-mp="2mm0t7no38nxh">অনুমোদন নিশ্চিত করুন</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === 'return' || modal === 'reject'} onOpenChange={o => !o && setModal(null)} data-id="element-772" data-mp="1wl8nafgpvga">
        <DialogContent data-id="element-773" data-mp="nzy2qbfatyib">
          <DialogHeader data-id="element-774" data-mp="nx3juobxolr0">
            <DialogTitle data-id="element-775" data-mp="j0lfvqofgrfq">{modal === 'return' ? 'সংশোধনের জন্য ফেরত' : 'নোটিশ প্রত্যাখ্যান'}</DialogTitle>
            <DialogDescription data-id="element-776" data-mp="xprhflaqmtqs">কারণ উল্লেখ করুন। সংশ্লিষ্ট কর্মকর্তাকে নোটিফিকেশন পাঠানো হবে।</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-4" data-id="element-777" data-mp="swycoxf9rlep">
            <Textarea rows={4} value={reason} onChange={e => {
            setReason(e.target.value);
            setErr('');
          }} placeholder="কারণ লিখুন" data-id="element-778" data-mp="16pxq8tvhr5jm" />
            {err && <p className="mt-1 text-xs text-[#D93036]" data-id="element-779" data-mp="11pt9r3ul2ate">{err}</p>}
          </div>
          <DialogFooter data-id="element-780" data-mp="1gon8inraus0e">
            <Button variant="outline" onClick={() => setModal(null)} data-id="element-781" data-mp="1gmsorcrrl5xw">বাতিল</Button>
            <Button variant={modal === 'reject' ? 'destructive' : 'default'} onClick={() => decline(modal === 'reject' ? 'reject' : 'return')} data-id="element-782" data-mp="1bor1sgr7wmh5">নিশ্চিত করুন</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}